import { COUNTRY_CODES, NAME_TYPES, getCountryData } from './addressMetadata';

const US_COUNTRY = 'United States';

// libaddressinput's data carries name TYPES; the English words live in its
// UI layer, as they do here. Lowercase: they also go into error messages.
const NAME_WORDS = {
  city: {
    city: 'city',
    post_town: 'post town',
    suburb: 'suburb',
    district: 'district',
  },
  state: {
    province: 'province',
    state: 'state',
    county: 'county',
    island: 'island',
    parish: 'parish',
    department: 'department',
    emirate: 'emirate',
    area: 'area',
    prefecture: 'prefecture',
    do_si: 'province',
    oblast: 'oblast',
    district: 'district',
  },
  zip: {
    postal: 'postcode',
    zip: 'ZIP code',
    eircode: 'Eircode',
    pin: 'PIN code',
  },
};
const DEFAULT_NAME_TYPES = { city: 'city', state: 'province', zip: 'postal' };
const FIELD_TOKENS = { C: 'city', S: 'state', Z: 'zip' };

function normalizeName(name) {
  return String(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z]/g, '');
}

let codesByName = null;

// Atlance sends the English country name ("Austria"), not the ISO code.
// Intl.DisplayNames (and String#normalize) are missing on some legacy-build
// browsers — no lookup there, so those merchants keep the generic non-US form.
function getCountryCode(country) {
  const text = String(country).trim();
  if (/^[A-Za-z]{2}$/.test(text)) {
    return text.toUpperCase();
  }

  try {
    if (!codesByName) {
      const names = new Intl.DisplayNames(['en'], { type: 'region' });
      codesByName = {};
      COUNTRY_CODES.forEach(code => {
        codesByName[normalizeName(names.of(code))] = code;
      });
    }
    return codesByName[normalizeName(text)] || null;
  } catch (e) {
    return null;
  }
}

function capitalize(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

function getZipPattern(zip) {
  if (!zip) {
    return null;
  }

  try {
    return new RegExp(`^(?:${zip})$`, 'i');
  } catch (e) {
    return null;
  }
}

/**
 * The billing address shape for a merchant's country, from Google's
 * libaddressinput metadata: which of city/state/postcode show, in what rows,
 * under which words, which are required, and the postcode pattern.
 *
 * null = render today's form: no country / the US (unchanged), or a country
 * name this lookup can't resolve (the generic non-US form).
 */
export function getAddressFormat(country) {
  if (!country || country === US_COUNTRY) {
    return null;
  }

  const code = getCountryCode(country);
  const data = code && code !== 'US' ? getCountryData(code) : null;
  if (!data) {
    return null;
  }

  // Rows after the street line, in the country's order: Austria
  // "%Z %C" = one row, postcode then city, and no state at all.
  const tokenRows = (data.lfmt || '%N%n%O%n%A%n%C')
    .split('%n')
    .map(line =>
      Array.from(line.matchAll(/%([A-Z])/g))
        .map(match => match[1])
        .filter(token => FIELD_TOKENS[token]),
    )
    .filter(row => row.length > 0);

  const nameTypes = { ...DEFAULT_NAME_TYPES, ...NAME_TYPES[code] };
  const fields = {};
  tokenRows.flat().forEach(token => {
    const field = FIELD_TOKENS[token];
    const word = NAME_WORDS[field][nameTypes[field]] || field;
    fields[field] = {
      word,
      label: capitalize(word),
      // Atlance's /details rejects a blank city or postcode whatever the
      // country; the state follows the data (e.g. Spain requires a province).
      required:
        field === 'city' ||
        field === 'zip' ||
        Boolean(data.require?.includes(token)),
    };
  });

  return {
    rows: tokenRows.map(row => row.map(token => FIELD_TOKENS[token])),
    fields,
    zipPattern: getZipPattern(data.zip),
  };
}

// One example VAT ID per EU member state, keyed by ISO code. The prefix is
// the VAT one, not always the ISO code (Greece is EL). Atlance validates the
// number itself (python-stdnum) and only offers the field for EU countries.
const VAT_ID_EXAMPLES = {
  AT: 'ATU12345678',
  BE: 'BE0123456789',
  BG: 'BG123456789',
  CY: 'CY12345678X',
  CZ: 'CZ12345678',
  DE: 'DE123456789',
  DK: 'DK12345678',
  EE: 'EE123456789',
  ES: 'ESB12345678',
  FI: 'FI12345678',
  FR: 'FR12345678901',
  GR: 'EL123456789',
  HR: 'HR12345678901',
  HU: 'HU12345678',
  IE: 'IE1234567T',
  IT: 'IT12345678901',
  LT: 'LT123456789',
  LU: 'LU12345678',
  LV: 'LV12345678901',
  MT: 'MT12345678',
  NL: 'NL123456789B01',
  PL: 'PL1234567890',
  PT: 'PT123456789',
  RO: 'RO1234567890',
  SE: 'SE123456789001',
  SI: 'SI12345678',
  SK: 'SK1234567890',
};

/**
 * An example VAT ID in the merchant's country's format ("EL123456789" for
 * Greece), or null when the country doesn't resolve to an EU member state.
 */
export function getVatIdExample(country) {
  if (!country) {
    return null;
  }

  return VAT_ID_EXAMPLES[getCountryCode(country)] || null;
}
